import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiAward, FiDownload, FiPrinter, FiSearch, FiUsers,
  FiDollarSign, FiTrendingUp, FiChevronUp, FiChevronDown
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import { expandMultiPartyTransactions } from '../../utils/multiPartyHelper';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37', '#4A90D9', '#E67E22', '#2ECC71', '#9B59B6'];

const SalespersonPerformance = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();

  // Data states
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSalesperson, setSelectedSalesperson] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState('all');
  const [selectedAreaId, setSelectedAreaId] = useState('all');
  const [taxFilter, setTaxFilter] = useState('both');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState('totalSales');
  const [sortDirection, setSortDirection] = useState('desc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load customers
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const custRef = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(custRef, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  // Load employees
  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const empRows = await getEmployeesCached(activeCompany.id);
      setEmployees(empRows.filter(r => r.firstName != null)
        .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || ''))));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [activeCompany]);

  // Load regions
  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const snapshot = await getDocs(ref);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  // Load areas
  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const snapshot = await getDocs(ref);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadCustomers();
    loadEmployees();
    loadRegions();
    loadAreas();

    // Set default date range to current month
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, [loadFinancialYears, loadCustomers, loadEmployees, loadRegions, loadAreas]);

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // Cascading areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Load transactions (SI, SR, CR, BR - Posted only)
  const loadTransactions = async () => {
    if (!activeCompany?.id || !selectedFYId) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');

      // Query sale transactions (SI, NSI, PS, SR, NSR)
      const saleConditions = [
        where('tranTypeCode', 'in', ['SI', 'NSI', 'PS', 'SR', 'NSR']),
        where('financialYearId', '==', selectedFYId),
        where('status', 'in', ['Posted', 'Paid', 'Partial'])
      ];
      const saleQuery = query(transRef, ...saleConditions);
      const saleSnapshot = await getDocs(saleQuery);
      let saleTransactions = saleSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Query receipt transactions (CR, BR)
      const receiptConditions = [
        where('tranTypeCode', 'in', ['CR', 'BR']),
        where('financialYearId', '==', selectedFYId),
        where('status', '==', 'Posted')
      ];
      const receiptQuery = query(transRef, ...receiptConditions);
      const receiptSnapshot = await getDocs(receiptQuery);
      let receiptTransactions = receiptSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      let allTransactions = [...saleTransactions, ...receiptTransactions];

      // Expand multi-party transactions into per-party rows
      allTransactions = expandMultiPartyTransactions(allTransactions);

      // Apply date filters
      if (dateFrom) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') <= dateTo);
      }

      // Apply salesperson filter
      if (selectedSalesperson) {
        allTransactions = allTransactions.filter(t => (t.employeeId || t.employeeId) === selectedSalesperson);
      }

      // Apply customer filter
      if (selectedCustomer) {
        allTransactions = allTransactions.filter(t => t.partyId === selectedCustomer);
      }

      // Apply region filter
      if (selectedRegionId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.regionId === selectedRegionId;
        });
      }

      // Apply area filter
      if (selectedAreaId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.areaId === selectedAreaId;
        });
      }

      // Tax filter (applies to sale invoices and returns only; receipts always pass)
      if (taxFilter === 'tax') {
        allTransactions = allTransactions.filter(t => !['NSI', 'NSR'].includes(t.tranTypeCode));
      } else if (taxFilter === 'nonTax') {
        allTransactions = allTransactions.filter(t => !['SI', 'SR'].includes(t.tranTypeCode));
      }

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group transactions by salesperson
  const salespersonData = useMemo(() => {
    if (transactions.length === 0) return [];

    const grouped = {};

    transactions.forEach(t => {
      const spId = t.employeeId || t.employeeId || '_unassigned';
      const spName = t.employeeName || t.employeeName || 'Unassigned';

      if (!grouped[spId]) {
        grouped[spId] = {
          salesPersonId: spId,
          salesPersonName: spName,
          totalSales: 0,
          totalReturns: 0,
          totalCollections: 0,
          netSales: 0,
          invoiceCount: 0,
          returnCount: 0,
          collectionCount: 0,
          customerSet: new Set(),
          avgInvoiceValue: 0
        };
      }

      const amount = Number(t.netAmount) || Number(t.totalAmount) || 0;

      if (['SI', 'NSI', 'PS'].includes(t.tranTypeCode)) {
        grouped[spId].totalSales += amount;
        grouped[spId].invoiceCount += 1;
      } else if (['SR', 'NSR'].includes(t.tranTypeCode)) {
        grouped[spId].totalReturns += amount;
        grouped[spId].returnCount += 1;
      } else if (t.tranTypeCode === 'CR' || t.tranTypeCode === 'BR') {
        grouped[spId].totalCollections += amount;
        grouped[spId].collectionCount += 1;
      }

      if (t.partyId) {
        grouped[spId].customerSet.add(t.partyId);
      }
    });

    // Calculate derived fields
    const results = Object.values(grouped).map(sp => {
      sp.netSales = sp.totalSales - sp.totalReturns;
      sp.customerCount = sp.customerSet.size;
      sp.avgInvoiceValue = sp.invoiceCount > 0 ? sp.totalSales / sp.invoiceCount : 0;
      delete sp.customerSet;

      // Commission calculation
      const employee = employees.find(e => e.id === sp.salesPersonId);
      sp.commissionType = employee?.commissionType || '';
      sp.commissionRate = employee?.commissionRate || 0;
      if (employee?.commissionType === 'Percentage') {
        sp.commissionEarned = sp.netSales * (sp.commissionRate / 100);
      } else if (employee?.commissionType === 'Fixed') {
        sp.commissionEarned = sp.invoiceCount * sp.commissionRate;
      } else {
        sp.commissionEarned = 0;
      }

      return sp;
    });

    return results;
  }, [transactions, employees]);

  // Grand totals
  const grandTotals = useMemo(() => {
    return salespersonData.reduce((totals, sp) => {
      totals.totalSales += sp.totalSales;
      totals.totalReturns += sp.totalReturns;
      totals.totalCollections += sp.totalCollections;
      totals.netSales += sp.netSales;
      totals.invoiceCount += sp.invoiceCount;
      totals.customerCount += sp.customerCount;
      totals.commissionEarned += sp.commissionEarned || 0;
      return totals;
    }, {
      totalSales: 0,
      totalReturns: 0,
      totalCollections: 0,
      netSales: 0,
      invoiceCount: 0,
      customerCount: 0,
      commissionEarned: 0
    });
  }, [salespersonData]);

  // Summary cards
  const summary = useMemo(() => {
    const totalSalespersons = salespersonData.filter(sp => sp.salesPersonId !== '_unassigned').length;
    const avgSalePerPerson = totalSalespersons > 0 ? grandTotals.totalSales / totalSalespersons : 0;

    return {
      totalSalespersons,
      totalSales: grandTotals.totalSales,
      totalCollections: grandTotals.totalCollections,
      avgSalePerPerson
    };
  }, [salespersonData, grandTotals]);

  // Sorted data
  const sortedData = useMemo(() => {
    const sorted = [...salespersonData];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        // numeric sort
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [salespersonData, sortField, sortDirection]);

  // Bar chart data - top performers by sales
  const barChartData = useMemo(() => {
    return [...salespersonData]
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 10)
      .map(sp => ({
        name: sp.salesPersonName.length > 15
          ? sp.salesPersonName.substring(0, 15) + '...'
          : sp.salesPersonName,
        fullName: sp.salesPersonName,
        totalSales: sp.totalSales,
        collections: sp.totalCollections,
        netSales: sp.netSales
      }));
  }, [salespersonData]);

  // Pie chart data - sales share
  const pieChartData = useMemo(() => {
    if (salespersonData.length === 0) return [];
    return [...salespersonData]
      .sort((a, b) => b.totalSales - a.totalSales)
      .map(sp => ({
        name: sp.salesPersonName,
        value: sp.totalSales
      }))
      .filter(sp => sp.value > 0);
  }, [salespersonData]);

  // Radar chart data - comparing top 5 salespersons across metrics
  const radarChartData = useMemo(() => {
    if (salespersonData.length < 2) return { data: [], persons: [] };

    const top5 = [...salespersonData]
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 5);

    // Normalize each metric to 0-100 scale
    const maxSales = Math.max(...top5.map(sp => sp.totalSales), 1);
    const maxCollections = Math.max(...top5.map(sp => sp.totalCollections), 1);
    const maxInvoices = Math.max(...top5.map(sp => sp.invoiceCount), 1);
    const maxCustomers = Math.max(...top5.map(sp => sp.customerCount), 1);
    const maxAvg = Math.max(...top5.map(sp => sp.avgInvoiceValue), 1);

    const metrics = ['Sales', 'Collections', 'Invoices', 'Customers', 'Avg Value'];
    const data = metrics.map((metric, idx) => {
      const entry = { metric };
      top5.forEach(sp => {
        let val = 0;
        if (idx === 0) val = (sp.totalSales / maxSales) * 100;
        if (idx === 1) val = (sp.totalCollections / maxCollections) * 100;
        if (idx === 2) val = (sp.invoiceCount / maxInvoices) * 100;
        if (idx === 3) val = (sp.customerCount / maxCustomers) * 100;
        if (idx === 4) val = (sp.avgInvoiceValue / maxAvg) * 100;
        entry[sp.salesPersonName] = Math.round(val);
      });
      return entry;
    });

    return { data, persons: top5.map(sp => sp.salesPersonName) };
  }, [salespersonData]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FiChevronDown size={12} style={{ opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />;
  };

  // CSV Export
  const handleExport = () => {
    if (sortedData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Salesperson Performance Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    csv += `Total Salespersons,${summary.totalSalespersons}\n`;
    csv += `Total Sales,${summary.totalSales}\n`;
    csv += `Total Collections,${summary.totalCollections}\n`;
    csv += `Avg Sale per Person,${summary.avgSalePerPerson}\n\n`;
    csv += '#,Salesperson,Sales Count,Total Sales,Returns,Net Sales,Collections,Customers,Avg Invoice,Comm. Rate,Commission,% of Total\n';

    sortedData.forEach((sp, idx) => {
      const pctOfTotal = grandTotals.totalSales > 0
        ? ((sp.totalSales / grandTotals.totalSales) * 100).toFixed(1)
        : '0.0';
      csv += `${idx + 1},`;
      csv += `"${(sp.salesPersonName || '').replace(/"/g, '""')}",`;
      csv += `${sp.invoiceCount},`;
      csv += `${sp.totalSales.toFixed(2)},`;
      csv += `${sp.totalReturns.toFixed(2)},`;
      csv += `${sp.netSales.toFixed(2)},`;
      csv += `${sp.totalCollections.toFixed(2)},`;
      csv += `${sp.customerCount},`;
      csv += `${sp.avgInvoiceValue.toFixed(2)},`;
      csv += `${sp.commissionRate > 0 ? sp.commissionRate + (sp.commissionType === 'Percentage' ? '%' : ' Fixed') : '-'},`;
      csv += `${(sp.commissionEarned || 0).toFixed(2)},`;
      csv += `${pctOfTotal}%\n`;
    });

    // Totals row
    csv += `\n,Totals,${grandTotals.invoiceCount},${grandTotals.totalSales.toFixed(2)},`;
    csv += `${grandTotals.totalReturns.toFixed(2)},${grandTotals.netSales.toFixed(2)},`;
    csv += `${grandTotals.totalCollections.toFixed(2)},${grandTotals.customerCount},,-,${grandTotals.commissionEarned.toFixed(2)},100%\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Salesperson_Performance_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    if (sortedData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Salesperson Performance Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        .rank{display:inline-block;width:22px;height:22px;line-height:22px;text-align:center;border-radius:50%;font-size:10px;font-weight:600}
        .rank-1{background:#FFD700;color:#333}
        .rank-2{background:#C0C0C0;color:#333}
        .rank-3{background:#CD7F32;color:white}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Salesperson Performance Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'} |
        Salespersons: ${summary.totalSalespersons}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Total Salespersons</div><div class="summary-val">${summary.totalSalespersons}</div></div>
        <div class="summary-box"><div class="summary-label">Total Sales</div><div class="summary-val">${formatCurrency(summary.totalSales)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Collections</div><div class="summary-val">${formatCurrency(summary.totalCollections)}</div></div>
        <div class="summary-box"><div class="summary-label">Avg Sale/Person</div><div class="summary-val">${formatCurrency(summary.avgSalePerPerson)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Salesperson</th><th class="right">Sales Count</th><th class="right">Total Sales</th>
          <th class="right">Returns</th><th class="right">Net Sales</th><th class="right">Collections</th>
          <th class="right">Customers</th><th class="right">Avg Invoice</th><th class="right">Comm. Rate</th><th class="right">Commission</th><th class="right">% of Total</th>
        </tr></thead>
        <tbody>
          ${sortedData.map((sp, idx) => {
            const pct = grandTotals.totalSales > 0 ? ((sp.totalSales / grandTotals.totalSales) * 100).toFixed(1) : '0.0';
            const rankClass = idx === 0 ? 'rank-1' : idx === 1 ? 'rank-2' : idx === 2 ? 'rank-3' : '';
            return `
              <tr>
                <td><span class="rank ${rankClass}">${idx + 1}</span></td>
                <td>${sp.salesPersonName || '-'}</td>
                <td class="right">${sp.invoiceCount}</td>
                <td class="right">${formatCurrency(sp.totalSales)}</td>
                <td class="right">${formatCurrency(sp.totalReturns)}</td>
                <td class="right">${formatCurrency(sp.netSales)}</td>
                <td class="right">${formatCurrency(sp.totalCollections)}</td>
                <td class="right">${sp.customerCount}</td>
                <td class="right">${formatCurrency(sp.avgInvoiceValue)}</td>
                <td class="right">${sp.commissionRate > 0 ? sp.commissionRate + (sp.commissionType === 'Percentage' ? '%' : ' Fixed') : '-'}</td>
                <td class="right">${sp.commissionEarned > 0 ? formatCurrency(sp.commissionEarned) : '-'}</td>
                <td class="right">${pct}%</td>
              </tr>`;
          }).join('')}
          <tr class="totals-row">
            <td colspan="2" style="text-align:right">Totals (${sortedData.length} salespersons)</td>
            <td class="right">${grandTotals.invoiceCount}</td>
            <td class="right">${formatCurrency(grandTotals.totalSales)}</td>
            <td class="right">${formatCurrency(grandTotals.totalReturns)}</td>
            <td class="right">${formatCurrency(grandTotals.netSales)}</td>
            <td class="right">${formatCurrency(grandTotals.totalCollections)}</td>
            <td class="right">${grandTotals.customerCount}</td>
            <td class="right">-</td>
            <td class="right">-</td>
            <td class="right">${formatCurrency(grandTotals.commissionEarned)}</td>
            <td class="right">100%</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltips
  const BarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const item = payload[0]?.payload;
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{item?.fullName || label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const PieTooltipContent = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      const pct = grandTotals.totalSales > 0
        ? ((entry.value / grandTotals.totalSales) * 100).toFixed(1)
        : '0.0';
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{entry.name}</ChartTooltipLabel>
          <ChartTooltipRow>
            <span>Sales: </span>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
          <ChartTooltipRow>
            <span>Share: </span>
            <strong>{pct}%</strong>
          </ChartTooltipRow>
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const RadarTooltipContent = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{entry.value}%</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Salesperson Performance" breadcrumb="Reports / Sale Reports / Salesperson Performance">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Salesperson</Label>
            <Select value={selectedSalesperson} onChange={e => setSelectedSalesperson(e.target.value)}>
              <option value="">All Salespersons</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeCode ? `${emp.employeeCode} - ` : ''}{emp.fullName || emp.firstName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomer}
              onChange={setSelectedCustomer}
              allOption={{ value: '', label: 'All Customers' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={sortedData.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={sortedData.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadTransactions} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIconBox $color={colors.primary.main}><FiUsers /></SummaryIconBox>
            <SummaryInfo>
              <SummaryLabel>Total Salespersons</SummaryLabel>
              <SummaryValue>{formatNumber(summary.totalSalespersons)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIconBox $color="#28A745"><FiDollarSign /></SummaryIconBox>
            <SummaryInfo>
              <SummaryLabel>Total Sales</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{formatCurrency(summary.totalSales)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIconBox $color={colors.chart.accent3}><FiTrendingUp /></SummaryIconBox>
            <SummaryInfo>
              <SummaryLabel>Total Collections</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalCollections)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIconBox $color={colors.chart.accent1}><FiAward /></SummaryIconBox>
            <SummaryInfo>
              <SummaryLabel>Avg Sale per Person</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.avgSalePerPerson)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiAward /></EmptyIcon>
              <EmptyText>Loading salesperson data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiAward /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view salesperson performance</EmptyText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiAward /></EmptyIcon>
              <EmptyText>No salesperson data found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <ThSortable
                    onClick={() => handleSort('salesPersonName')}
                    $active={sortField === 'salesPersonName'}
                  >
                    Salesperson
                    <SortIconSpan>{getSortIcon('salesPersonName')}</SortIconSpan>
                  </ThSortable>
                  <ThSortable
                    $right
                    onClick={() => handleSort('invoiceCount')}
                    $active={sortField === 'invoiceCount'}
                  >
                    Sales Count
                    <SortIconSpan>{getSortIcon('invoiceCount')}</SortIconSpan>
                  </ThSortable>
                  <ThSortable
                    $right
                    onClick={() => handleSort('totalSales')}
                    $active={sortField === 'totalSales'}
                  >
                    Total Sales
                    <SortIconSpan>{getSortIcon('totalSales')}</SortIconSpan>
                  </ThSortable>
                  <ThSortable
                    $right
                    onClick={() => handleSort('totalReturns')}
                    $active={sortField === 'totalReturns'}
                  >
                    Returns
                    <SortIconSpan>{getSortIcon('totalReturns')}</SortIconSpan>
                  </ThSortable>
                  <ThSortable
                    $right
                    onClick={() => handleSort('netSales')}
                    $active={sortField === 'netSales'}
                  >
                    Net Sales
                    <SortIconSpan>{getSortIcon('netSales')}</SortIconSpan>
                  </ThSortable>
                  <ThSortable
                    $right
                    onClick={() => handleSort('totalCollections')}
                    $active={sortField === 'totalCollections'}
                  >
                    Collections
                    <SortIconSpan>{getSortIcon('totalCollections')}</SortIconSpan>
                  </ThSortable>
                  <ThSortable
                    $right
                    onClick={() => handleSort('customerCount')}
                    $active={sortField === 'customerCount'}
                  >
                    Customers
                    <SortIconSpan>{getSortIcon('customerCount')}</SortIconSpan>
                  </ThSortable>
                  <ThSortable
                    $right
                    onClick={() => handleSort('avgInvoiceValue')}
                    $active={sortField === 'avgInvoiceValue'}
                  >
                    Avg Invoice
                    <SortIconSpan>{getSortIcon('avgInvoiceValue')}</SortIconSpan>
                  </ThSortable>
                  <Th $right>Comm. Rate</Th>
                  <ThSortable
                    $right
                    onClick={() => handleSort('commissionEarned')}
                    $active={sortField === 'commissionEarned'}
                  >
                    Commission
                    <SortIconSpan>{getSortIcon('commissionEarned')}</SortIconSpan>
                  </ThSortable>
                  <Th $right>% of Total</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedData.map((sp, idx) => {
                  const pctOfTotal = grandTotals.totalSales > 0
                    ? ((sp.totalSales / grandTotals.totalSales) * 100).toFixed(1)
                    : '0.0';

                  return (
                    <Tr key={sp.salesPersonId}>
                      <Td>
                        <RankBadge $rank={idx + 1}>
                          {idx + 1}
                        </RankBadge>
                      </Td>
                      <Td>
                        <SalespersonLink onClick={() => navigate('/reports/sale-register', { state: { filterSalesperson: sp.salesPersonId } })}>
                          {sp.salesPersonName}
                        </SalespersonLink>
                      </Td>
                      <AmountCell $right>{formatNumber(sp.invoiceCount)}</AmountCell>
                      <AmountCell $right>{formatCurrency(sp.totalSales)}</AmountCell>
                      <AmountCell $right style={{ color: sp.totalReturns > 0 ? '#DC3545' : undefined }}>
                        {formatCurrency(sp.totalReturns)}
                      </AmountCell>
                      <AmountCell $right style={{ fontWeight: 600 }}>
                        {formatCurrency(sp.netSales)}
                      </AmountCell>
                      <AmountCell $right>{formatCurrency(sp.totalCollections)}</AmountCell>
                      <AmountCell $right>{formatNumber(sp.customerCount)}</AmountCell>
                      <AmountCell $right>{formatCurrency(sp.avgInvoiceValue)}</AmountCell>
                      <AmountCell $right style={{ fontSize: '12px', color: colors.text.secondary }}>
                        {sp.commissionRate > 0 ? `${sp.commissionRate}${sp.commissionType === 'Percentage' ? '%' : ' (Fixed)'}` : '-'}
                      </AmountCell>
                      <AmountCell $right style={{ fontWeight: 600, color: sp.commissionEarned > 0 ? colors.success.main : undefined }}>
                        {sp.commissionEarned > 0 ? formatCurrency(sp.commissionEarned) : '-'}
                      </AmountCell>
                      <Td $right>
                        <PercentBar>
                          <PercentFill style={{ width: `${Math.min(parseFloat(pctOfTotal), 100)}%` }} />
                          <PercentText>{pctOfTotal}%</PercentText>
                        </PercentBar>
                      </Td>
                    </Tr>
                  );
                })}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedData.length} salespersons)
                  </Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>
                    {formatNumber(grandTotals.invoiceCount)}
                  </AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>
                    {formatCurrency(grandTotals.totalSales)}
                  </AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>
                    {formatCurrency(grandTotals.totalReturns)}
                  </AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>
                    {formatCurrency(grandTotals.netSales)}
                  </AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>
                    {formatCurrency(grandTotals.totalCollections)}
                  </AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>
                    {formatNumber(grandTotals.customerCount)}
                  </AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>-</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>-</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>
                    {formatCurrency(grandTotals.commissionEarned)}
                  </AmountCell>
                  <Td $right style={{ fontWeight: 700 }}>100%</Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasSearched && !loading && salespersonData.length > 0 && (
        <ChartsRow>
          {/* Bar Chart - Top Performers */}
          <ChartSection>
            <ChartTitle>Top Performers - Sales by Salesperson</ChartTitle>
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: colors.neutral[300] }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: colors.neutral[300] }}
                    tickFormatter={(v) => formatCurrency(v, { compact: true, symbol: '' })}
                  />
                  <Tooltip content={<BarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="totalSales" name="Total Sales" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="collections" name="Collections" fill={CHART_COLORS[3]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Pie Chart - Sales Share */}
          <ChartSection>
            <ChartTitle>Sales Share by Salesperson</ChartTitle>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name.length > 12 ? name.substring(0, 12) + '...' : name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Radar Chart - Multi-metric Comparison */}
          {radarChartData.data.length > 0 && radarChartData.persons.length >= 2 && (
            <ChartSection $wide>
              <ChartTitle>Performance Comparison (Top 5 - Normalized %)</ChartTitle>
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={radarChartData.data} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke={colors.neutral[200]} />
                  <PolarAngleAxis
                    dataKey="metric"
                    tick={{ fontSize: 12, fill: colors.text.secondary }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 100]}
                    tick={{ fontSize: 10, fill: colors.text.tertiary }}
                  />
                  <Tooltip content={<RadarTooltipContent />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {radarChartData.persons.map((person, idx) => (
                    <Radar
                      key={person}
                      name={person}
                      dataKey={person}
                      stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                      fill={CHART_COLORS[idx % CHART_COLORS.length]}
                      fillOpacity={0.15}
                      strokeWidth={2}
                    />
                  ))}
                </RadarChart>
              </ResponsiveContainer>
            </ChartSection>
          )}
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;
`;

const ThSortable = styled(Th)`
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.main};
  }

  ${props => props.$active && `
    color: ${colors.primary.main};
  `}
`;

const SortIconSpan = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
`;

const SalespersonLink = styled.span`
  color: #1a73e8;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.dark};
    text-decoration: underline;
  }
`;

const RankBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;

  ${props => {
    if (props.$rank === 1) return `background: #FFD700; color: #333;`;
    if (props.$rank === 2) return `background: #C0C0C0; color: #333;`;
    if (props.$rank === 3) return `background: #CD7F32; color: white;`;
    return `background: ${colors.neutral[200]}; color: ${colors.text.secondary};`;
  }}
`;

const PercentBar = styled.div`
  position: relative;
  background: ${colors.neutral[100]};
  border-radius: 4px;
  height: 22px;
  min-width: 80px;
  overflow: hidden;
`;

const PercentFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: ${colors.primary.main}25;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const PercentText = styled.span`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default SalespersonPerformance;
